/**
 * "Post to channel" and "Notify staff" — the TRUS-686 workflow steps — and
 * the message-template rules they share with automation rule actions
 * (system_events_contract_v2.md sections D and E).
 *
 * Step configs, exactly as the backend validates them:
 *   notify_channel: { channel_id, message_template, include_link }
 *   notify_staff:   { recipients: { assignee?, case_manager?, roles?, user_ids? },
 *                     message_template, channels: ["bell" | "push"] }
 *
 * Templates may only use the allow-list below. Anything else is left blank by
 * the server, so the builder flags it before save.
 */

export const NOTIFY_CHANNEL_STEP = 'notify_channel';
export const NOTIFY_STAFF_STEP = 'notify_staff';
export type NotifyStepType = typeof NOTIFY_CHANNEL_STEP | typeof NOTIFY_STAFF_STEP;

export const isNotifyStep = (stepType: string | null | undefined): stepType is NotifyStepType =>
  stepType === NOTIFY_CHANNEL_STEP || stepType === NOTIFY_STAFF_STEP;

/** The safe allow-list (contract D). No other member fields, ever. */
export const TEMPLATE_VARIABLES = [
  'program_name',
  'step_name',
  'workflow_name',
  'due_date',
  'assignee_name',
  'member_first_initial',
  'link',
] as const;
export type TemplateVariable = (typeof TEMPLATE_VARIABLES)[number];

const VAR_RE = /\{\{\s*([^{}]*?)\s*\}\}/g;

/** Every `{{name}}` in the text, in order, de-duplicated. */
export function templateVariablesIn(text: string | null | undefined): string[] {
  const out: string[] = [];
  for (const m of String(text || '').matchAll(VAR_RE)) {
    const name = m[1];
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

/** Variables the server would leave blank. */
export function unknownTemplateVariables(text: string | null | undefined): string[] {
  return templateVariablesIn(text).filter((v) => !(TEMPLATE_VARIABLES as readonly string[]).includes(v));
}

export interface StaffRecipients {
  assignee?: boolean;
  case_manager?: boolean;
  roles?: string[];
  user_ids?: number[];
}

export const hasRecipients = (r: StaffRecipients | null | undefined): boolean =>
  !!r && (r.assignee === true || r.case_manager === true || (r.roles?.length ?? 0) > 0 || (r.user_ids?.length ?? 0) > 0);

export type NotifyIssueCode =
  | 'channel_required'
  | 'message_required'
  | 'unknown_variables'
  | 'recipients_required'
  | 'delivery_required';

export interface NotifyIssue {
  code: NotifyIssueCode;
  /** Only for unknown_variables. */
  variables?: string[];
}

/** What is wrong with a notify step's config (empty = ready to save). */
export function notifyStepIssues(stepType: string, config: Record<string, any> | null | undefined): NotifyIssue[] {
  if (!isNotifyStep(stepType)) return [];
  const c = config || {};
  const issues: NotifyIssue[] = [];
  if (stepType === NOTIFY_CHANNEL_STEP && !(Number(c.channel_id) > 0)) issues.push({ code: 'channel_required' });
  if (stepType === NOTIFY_STAFF_STEP) {
    if (!hasRecipients(c.recipients)) issues.push({ code: 'recipients_required' });
    if (!Array.isArray(c.channels) || c.channels.length === 0) issues.push({ code: 'delivery_required' });
  }
  if (!String(c.message_template || '').trim()) issues.push({ code: 'message_required' });
  const unknown = unknownTemplateVariables(c.message_template);
  if (unknown.length) issues.push({ code: 'unknown_variables', variables: unknown });
  return issues;
}

/** Default config for a freshly added step. */
export function defaultNotifyConfig(stepType: NotifyStepType): Record<string, any> {
  return stepType === NOTIFY_CHANNEL_STEP
    ? { channel_id: null, message_template: '', include_link: true }
    : { recipients: { assignee: true }, message_template: '', channels: ['bell', 'push'] };
}

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

export function notifyIssueMessage(issue: NotifyIssue, t: TFn): string {
  switch (issue.code) {
    case 'channel_required':
      return t('workflows.notify.issues.channelRequired', 'Pick the channel to post to.');
    case 'message_required':
      return t('workflows.notify.issues.messageRequired', 'Write the message.');
    case 'recipients_required':
      return t('workflows.notify.issues.recipientsRequired', 'Pick at least one recipient.');
    case 'delivery_required':
      return t('workflows.notify.issues.deliveryRequired', 'Turn on the bell, push, or both.');
    case 'unknown_variables':
      return t(
        'workflows.notify.issues.unknownVariables',
        'These variables are not allowed and would be left blank: {{list}}',
        { list: (issue.variables || []).map((v) => `{{${v}}}`).join(', ') },
      );
    default:
      return '';
  }
}

/** The 400 phi_requires_private_channel sentence, shared by the builder pages. */
export function phiRequiresPrivateMessage(t: TFn): string {
  return t(
    'workflows.notify.errors.phiRequiresPrivate',
    'This workflow is about members, so "Post to channel" can only post to a private channel. Pick a private channel and save again.',
  );
}

/** The backend's error code on a failed save, if any. */
export function apiErrorCode(err: any): string | null {
  const data = err?.response?.data;
  const detail = data?.detail;
  if (typeof data?.code === 'string') return data.code;
  if (typeof detail?.code === 'string') return detail.code;
  if (typeof detail === 'string' && /^[a-z_]+$/.test(detail)) return detail;
  return null;
}

/** A readable sentence from a failed request: detail string, detail.message, or the fallback. */
export function apiErrorText(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof detail?.message === 'string' && detail.message.trim()) return detail.message;
  return fallback;
}

/**
 * A plain sentence for a failed workflow-template save (TRUS-686 step
 * validation codes), else the server's detail, else the fallback.
 */
export function workflowSaveErrorMessage(err: any, t: TFn, fallback: string): string {
  switch (apiErrorCode(err)) {
    case 'phi_requires_private_channel':
      return phiRequiresPrivateMessage(t);
    case 'unknown_template_variable':
      return t(
        'workflows.notify.errors.unknownVariable',
        'A message uses a variable that is not allowed. Use only the variables offered under the message box.',
      );
    case 'invalid_step_config':
      return t('workflows.notify.errors.invalidStepConfig', 'A "Post to channel" or "Notify staff" step is not set up completely. Open it and check every field.');
    case 'no_recipients':
      return t('workflows.notify.errors.noRecipients', 'A "Notify staff" step has nobody to notify. Pick at least one recipient.');
    case 'invalid_channels':
      return t('workflows.notify.errors.invalidChannels', 'A "Notify staff" step needs the bell, push, or both.');
    default:
      return apiErrorText(err, fallback);
  }
}

/** The first notify step that is not ready to save, as a sentence (null when all are fine). */
export function firstNotifyStepProblem(
  steps: Array<{ name?: string; step_type: string; config?: Record<string, any> }>,
  t: TFn,
): string | null {
  for (const step of steps) {
    const issues = notifyStepIssues(step.step_type, step.config);
    if (issues.length) {
      return t('workflows.notify.errors.stepNotReady', 'Step "{{name}}": {{problem}}', {
        name: step.name || step.step_type,
        problem: notifyIssueMessage(issues[0], t),
      });
    }
  }
  return null;
}
